import { AuthService } from 'src/app/core/authentication/auth.service';
import { UiNotificationService } from 'src/app/external-ui-shared/services/ui-notification.service';
import { PortalUserFormBuilder } from 'src/app/feature-module/portal-users/models/PortalUser';
import { PortalUserProfile } from 'src/app/feature-module/portal-users/models/PortalUserProfile';
import { PortalUserService } from 'src/app/feature-module/portal-users/services/portalUser.service';
import { Constants } from 'src/app/shared/model/Constants';
import { CustomDialogSettings } from 'src/app/shared/model/CustomDialogSettings';
import { Roles } from 'src/app/shared/model/Roles';
import { CustomDialogService } from 'src/app/shared/services/custom-dialog.service';
import { FormUtilsService } from 'src/app/shared/services/form-utils.service';

import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DialogResult } from '@progress/kendo-angular-dialog';

import { AddUserAbilityComponent } from '../add-user-ability/add-user-ability.component';
import { AddUserRestrictionComponent } from '../add-user-restriction/add-user-restriction.component';
import { AddUserRoleComponent } from '../add-user-role/add-user-role.component';

@Component({
  selector: 'app-user-manage',
  templateUrl: './user-manage.component.html',
  styleUrls: ['./user-manage.component.scss']
})
export class UserManageComponent implements OnInit {

  editDataForm: UntypedFormGroup;
  linkSent: boolean;
  superAdminRoleId = Constants.SuperAdminRoleId;


  constructor(private activatedRoute: ActivatedRoute,
    private userService: PortalUserService,
    private authService: AuthService,
    private fb: UntypedFormBuilder,
    private customDialogService: CustomDialogService,
    public uiNotification: UiNotificationService,
    private formUtilsService: FormUtilsService) { }

  userProfile: PortalUserProfile;
  isAdmin:boolean;

  @ViewChild("userManageDialog", { read: ViewContainerRef })
  public containerRef: ViewContainerRef;



  ngOnInit(): void {
    this.loadData();
    this.isAdmin = this.authService.hasRole(Roles.SuperAdmin);
  }

  loadData() {

    this.editDataForm = PortalUserFormBuilder.getForm(this.fb, PortalUserFormBuilder.initPortalUser());

    this.userService.getUserProfile(this.activatedRoute.snapshot.params.id).subscribe(data => {
      this.userProfile = data;
      this.editDataForm = PortalUserFormBuilder.getForm(this.fb, this.userProfile.portalUser);
    });
  }

  update() {

    if (!this.formUtilsService.markFormGroupTouched(this.editDataForm)) {
      this.uiNotification.showValidationError();
      return;
    }


    Object.assign(this.userProfile.portalUser, this.editDataForm.value);

    this.userService.updateUserProfile(this.userProfile.portalUser).subscribe(
      () => {
        this.uiNotification.showSuccess('validations.dataStoredSuccesfully');
        this.loadData();
      }
    );
  }

  removeRole(roleId) {
    this.userService.removeUserRole(this.userProfile.portalUser.id, roleId).subscribe(
      () => {
        this.uiNotification.showSuccess('validations.userRoleRemoved');
        this.loadData();
      }
    );
  }

  removeAbility(abilityId) {
    this.userService.removeUserAbility(this.userProfile.portalUser.id, abilityId).subscribe(
       () => {
        this.uiNotification.showSuccess('validations.userAbilityRemoved');
        this.loadData();
      }
    );
  }

  removeRestriction(restrictionId) {
    this.userService.removeUserRestriction(this.userProfile.portalUser.id, restrictionId).subscribe(
      () => {
        this.uiNotification.showSuccess('validations.userRestrictionRemoved');
        this.loadData();
      }
    );
  }


  addRole() {

    const dialogSettings: CustomDialogSettings = {
      content: AddUserRoleComponent,
      appendTo: this.containerRef,
      minWidth: '30vw',
      dialogInputs: {
        userId : this.userProfile.portalUser.id,
        excludeRoles : this.userProfile.userRoles.map(r => r.id)
      },
      resultCallback: (r: DialogResult) => {
        if (r['text'] == 'OK') {
          this.loadData();
        }
      }
    };

    this.customDialogService.showDialog(dialogSettings);

  }

  addAbility() {

    const dialogSettings: CustomDialogSettings = {
      content: AddUserAbilityComponent,
      appendTo: this.containerRef,
      minWidth: '30vw',
      dialogInputs: {
        userId : this.userProfile.portalUser.id,
        excludeAbilities :this.userProfile.userAbilities.map(a => a.id)
      },
      resultCallback: (r: DialogResult) => {
        if (r['text'] == 'OK') {
          this.loadData();
        }
      }
    };

    this.customDialogService.showDialog(dialogSettings);

  }

  addRestriction() {

    const dialogSettings: CustomDialogSettings = {
      content: AddUserRestrictionComponent,
      appendTo: this.containerRef,
      minWidth: '30vw',
      dialogInputs: {
        userId : this.userProfile.portalUser.id,
        excludeRestrictions : this.userProfile.userRestrictions.map(r => r.id)
      },
      resultCallback: (r: DialogResult) => {
        if (r['text'] == 'OK') {
          this.loadData();
        }
      }
    };

    this.customDialogService.showDialog(dialogSettings);

  }


  setUserEnable(ev: boolean) {
    this.userService.setPortalUserEnable(this.userProfile.portalUser.id, ev).subscribe(
      () => {
        const message = ev ? 'validations.userEnabled' : 'validations.userDisabled'
        this.uiNotification.showSuccess(message);
        this.loadData();
      });
  }

  unlockUser(ev: boolean) {
    if (ev) {
      this.userService.unlockPortalUser(this.userProfile.portalUser.id).subscribe(
        () => {
          const message = ev ? 'validations.userUnlocked' : 'validations.userLocked'
          this.uiNotification.showSuccess(message);
          this.loadData();
        }
      );
    }
  }

  sendResetPasswordLink() {
    const task = this.uiNotification.showRunningOperation('operations.sendingOngoing');

    this.userService.sendResetPasswordLink(this.userProfile.portalUser.id).subscribe(
       () => {
        this.uiNotification.showSuccess('validations.resetPasswordLinkWasSentSuccesfully');
        task.hide();
      }
    );
  }

  changePassword() {
    this.userService.getResetPasswordLink(this.userProfile.portalUser.id).subscribe(
      r => window.open(r.link, "_blank")      
    );
  }
}

